import { FileMetaDataCreateOrUpdateRequest, FileMetaDataDto, FileParameter } from '@/api/ApiClient';
import { useBackendStore } from '@/stores/backendStore';
import { useTree } from '@/hooks/useTree';
import { TreeNode } from '@/types/tree';
import { findAncestorPendingSnapshotDataPackage } from '@/lib/tree/findAncestorDataPackageContext';

/**
 * Optional multipart fields when the upload UI does not collect metadata (matches generated API client).
 *
 * The api allows for that to be set upon upload, but the UI would only collect them after the file is uploaded,
 * and set via update.
 * */
const uploadFormExtras = [null, null, null] as [null, null, null];

/** Config for file operations (upload/update/delete) for a single node type. */
export interface FileOperationConfigEntry {
  upload: (fileParameter: FileParameter) => Promise<FileMetaDataDto>;
  /** All owners use the same partial-merge write body (see server FileMetaDataCreateOrUpdateRequest). */
  update: (request: FileMetaDataCreateOrUpdateRequest) => Promise<FileMetaDataDto>;
  delete: (fileId: number) => Promise<void>;
  /**
   * Pending snapshot data package only: remove a whole logical file group from the snapshot activity.
   * Uses {@link fileGroupId} (FileMetaData id), not a physical {@link FileDto} row id — see server
   * `DELETE …/sample-activities/{activityId}/file-groups/{fileGroupId}` vs per-file `…/files/{fileId}`.
   */
  deleteSnapshotFileGroup?: (fileGroupId: number) => Promise<void>;
}

/**
 * Returns getFileConfig() for the given node. Use in components that need to perform
 * file upload/update/delete based on node type (e.g. FilesDetails).
 * Throws if node.type does not support files.
 * upload() throws with an entity-specific message (e.g. "Sample not found") if the parent entity data is missing.
 */
export function useFileOperationConfig(node: TreeNode) {
  const backend = useBackendStore((s) => s.getBackend());
  const {
    getNodeData,
    getSampleData,
    getSampleActivityData,
    getProjectActivityData,
    getTheoryActivityData,
    getCommunicationData,
    getShippingData,
    getDataPackageData,
    getTypedData: getTypedDataFromTree,
  } = useTree();

  const fileOperationConfig: Record<string, FileOperationConfigEntry> = {
    sample: {
      upload: async (fileParameter: FileParameter) => {
        const data = getSampleData(node);
        if (!data) throw new Error('Sample not found');
        return backend.sample.uploadSampleFileForm(data.sampleLabel!, fileParameter, ...uploadFormExtras);
      },
      update: async (request: FileMetaDataCreateOrUpdateRequest) => {
        const data = getSampleData(node);
        if (!data) throw new Error('Sample not found');
        return backend.sample.updateFileGroup(data.sampleLabel!, request);
      },
      delete: async (fileId: number) => {
        const data = getSampleData(node);
        if (!data) throw new Error('Sample not found');
        return backend.sample.deleteFile(fileId, data.sampleLabel!);
      },
    },
    sampleActivity: {
      upload: async (fileParameter: FileParameter) => {
        const data = getSampleActivityData(node);
        if (!data) throw new Error('Sample activity not found');
        return backend.sampleActivity.uploadActivityFileForm(data.id!, fileParameter, ...uploadFormExtras);
      },
      update: async (request: FileMetaDataCreateOrUpdateRequest) => {
        const data = getSampleActivityData(node);
        if (!data) throw new Error('Sample activity not found');
        return backend.sampleActivity.updateFileGroup(data.id!, request);
      },
      delete: async (fileId: number) => {
        const data = getSampleActivityData(node);
        if (!data) throw new Error('Sample activity not found');
        return backend.sampleActivity.deleteFile(fileId, data.id!);
      },
    },
    projectActivity: {
      upload: async (fileParameter: FileParameter) => {
        const data = getProjectActivityData(node);
        if (!data) throw new Error('Project activity not found');
        return backend.projectActivity.uploadActivityFileForm(data.id!, fileParameter, ...uploadFormExtras);
      },
      update: async (request: FileMetaDataCreateOrUpdateRequest) => {
        const data = getProjectActivityData(node);
        if (!data) throw new Error('Project activity not found');
        return backend.projectActivity.updateFileGroup(data.id!, request);
      },
      delete: async (fileId: number) => {
        const data = getProjectActivityData(node);
        if (!data) throw new Error('Project activity not found');
        return backend.projectActivity.deleteFile(fileId, data.id!);
      },
    },
    theoryActivity: {
      upload: async (fileParameter: FileParameter) => {
        const data = getTheoryActivityData(node);
        if (!data) throw new Error('Theory activity not found');
        return backend.theoryActivity.uploadActivityFileForm(data.id!, fileParameter, ...uploadFormExtras);
      },
      update: async (request: FileMetaDataCreateOrUpdateRequest) => {
        const data = getTheoryActivityData(node);
        if (!data) throw new Error('Theory activity not found');
        return backend.theoryActivity.updateFileGroup(data.id!, request);
      },
      delete: async (fileId: number) => {
        const data = getTheoryActivityData(node);
        if (!data) throw new Error('Theory activity not found');
        return backend.theoryActivity.deleteFile(fileId, data.id!);
      },
    },
    communication: {
      upload: async (fileParameter: FileParameter) => {
        const data = getCommunicationData(node);
        if (!data) throw new Error('Communication not found');
        return backend.communication.uploadCommunicationFileForm(data.id!, fileParameter, ...uploadFormExtras);
      },
      update: async (request: FileMetaDataCreateOrUpdateRequest) => {
        const data = getCommunicationData(node);
        if (!data) throw new Error('Communication not found');
        return backend.communication.updateFileGroup(data.id!, request);
      },
      delete: async (fileId: number) => {
        const data = getCommunicationData(node);
        if (!data) throw new Error('Communication not found');
        return backend.communication.deleteFile(fileId, data.id!);
      },
    },
    shipping: {
      upload: async (fileParameter: FileParameter) => {
        const data = getShippingData(node);
        if (!data) throw new Error('Shipping not found');
        return backend.shipping.uploadShippingFileForm(data.id!, fileParameter, ...uploadFormExtras);
      },
      update: async (request: FileMetaDataCreateOrUpdateRequest) => {
        const data = getShippingData(node);
        if (!data) throw new Error('Shipping not found');
        return backend.shipping.updateFileGroup(data.id!, request);
      },
      delete: async (fileId: number) => {
        const data = getShippingData(node);
        if (!data) throw new Error('Shipping not found');
        return backend.shipping.deleteFile(fileId, data.id!);
      },
    },
    dataPackage: {
      upload: async (fileParameter: FileParameter) => {
        const data = getDataPackageData(node);
        if (!data) throw new Error('Data package not found');
        return backend.data.uploadDataPackageFileForm(data.id!.toString(), fileParameter, JSON.stringify({}));
      },
      update: async (request: FileMetaDataCreateOrUpdateRequest) => {
        const data = getDataPackageData(node);
        if (!data) throw new Error('Data package not found');
        return backend.data.updateFileGroup(data.id!.toString(), request);
      },
      delete: async (fileId: number) => {
        const data = getDataPackageData(node);
        if (!data) throw new Error('Data package not found');
        return backend.data.deleteFile(fileId, data.id!.toString());
      },
    },
    /**
     * Snapshot-package activities: no live upload or metadata edit; optional per-file trim uses
     * {@link findAncestorPendingSnapshotDataPackage} + `DELETE …/data-packages/…/sample-activities/…/files/…`.
     */
    sampleActivitySnapshot: {
      upload: async () => {
        throw new Error('Upload is not available for activities inside a data package snapshot.');
      },
      update: async () => {
        throw new Error('File metadata edit is not available for activities inside a data package snapshot.');
      },
      delete: async (fileId: number) => {
        const ctx = findAncestorPendingSnapshotDataPackage(node, getNodeData, getTypedDataFromTree);
        const activity = getSampleActivityData(node);
        if (!ctx || activity?.id == null) {
          throw new Error('Removing this file is only allowed for a pending snapshot data package.');
        }
        return backend.data.removeFiles(ctx.dataPackageId, activity.id, fileId);
      },
      deleteSnapshotFileGroup: async (fileGroupId: number) => {
        const ctx = findAncestorPendingSnapshotDataPackage(node, getNodeData, getTypedDataFromTree);
        const activity = getSampleActivityData(node);
        if (!ctx || activity?.id == null) {
          throw new Error('Removing this file is only allowed for a pending snapshot data package.');
        }
        return backend.data.removeFileGroupFromSnapshot(ctx.dataPackageId, activity.id, fileGroupId);
      },
    },
  };

  const getFileConfig = (): FileOperationConfigEntry => {
    const config = fileOperationConfig[node.type];
    if (!config) {
      throw new Error(
        `Node type '${node.type}' does not support files. This component should not be rendered for this node type.`
      );
    }
    return config;
  };

  return { getFileConfig };
}
